import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import {
  FETCH_OPTIMAL_PORTFOLIO,
  FETCH_PORTFOLIO_REPORTS, FETCH_SUB_TRADE_REQUESTS,
  REFRESH_PORTFOLIO_REPORT,
} from '../../../../4-module/widgets/portfolio/components/queries';
import {
  FetchOptimalPortfolio,
  FetchPortfolioOptimizer,
  PortfolioReports,
  RefreshPortfolioReport,
} from './sharedTypes';
import {
  HoldingReport,
  OrgBulkTradingProcesses,
  PortfolioReport,
  PortfolioReportRefreshStates,
} from '../../../../../interfaces';
import {
  DraftSubTradeRequest,
  PortfolioSecurity,
  PortfolioSubAccount,
} from '../../../../4-module/widgets/portfolio/components/interfaces';
import { SubAccountReport } from '../../../../../interfaces/subAccountReport';
import {
  buildPortfolioSubAccount, buildSecurity,
  sortPortfolioReportSubAccounts, sortSecurities,
} from '../../../../4-module/widgets/portfolio/components/utils';
import { UserContext } from '../../../../../providers/userContextProvider';
import { SubTradeRequest, SubTradeRequestStates, SubTradeRequestTypes } from '../../../../../interfaces/subTradeRequest';
import { FetchSubTradeRequests } from './subTradeRequestsTypes';
import {
  addPortfolioWarning,
  buildIlliquidProduct,
  checkAndSetMFSellAllTicker,
  checkAndSetUsdTicker,
  IlliquidProductsMap,
  illiquidProductsToString,
  joinSetValues,
  PortfolioWarningKeys,
  PortfolioWarningsMap,
} from '../../../../4-module/widgets/portfolio/components/portfolioWarnings';
import { useLocalization } from '../../../../../util/useLocalization';
import { RUN_PORTFOLIO_OPTIMIZER } from '../../../../../pages/portfolioOptimizers/components/selectOptimizerModal';
import { FETCH_PORTFOLIO_OPTIMIZER } from '../../../../../pages/portfolioOptimizer';
import { PortfolioOptimizer, PortfolioOptimizerStates } from '../../../../../interfaces/portfolioOptimizer';
import { OptimalHolding, OptimalPortfolio } from '../../../../../interfaces/optimalPortfolio';
import { useGlobalToast } from '../../../../../providers/globalToastProvider';

const CREATE_SUB_TRADE_REQUESTS = gql`
  mutation createSubTradeRequests($input: CreateSubTradeRequestsInput!) {
    createSubTradeRequests(input: $input)
  }
`;

const PAIR_SUB_TRADE_REQUESTS = gql`
  mutation pairSubTradeRequests($input: PairSubTradeRequestsInput!) {
    pairSubTradeRequests(input: $input)
  }
`;

const BUCKET_SUB_TRADE_REQUESTS = gql`
  mutation bucketSubTradeRequests($input: BucketSubTradeRequestsInput!) {
    bucketSubTradeRequests(input: $input)
  }
`;

export const useSinglePortfolioModel = (id: string, urlPath: string) => {
  const { t } = useTranslation(['components', 'accountTypes', 'shared']);
  const { activeOrganization } = useContext(UserContext);
  const { localizedDate } = useLocalization();
  const { showToast } = useGlobalToast();

  const [loading, setLoading] = useState<boolean>(false);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [portfolio, setPortfolio] = useState<PortfolioReport | null>(null);
  const [subTradeRequests, setSubTradeRequests] = useState<SubTradeRequest[]>([]);
  const [optimizedPortfolio, setOptimizedPortfolio] = useState<OptimalPortfolio | null>(null);

  const [displayInactive, setDisplayInactive] = useState<boolean>(false);

  const queryVariables = useMemo(() => ({
    input: {
      filter: {
        subAccountId: urlPath.includes('subAccount') ? id : undefined,
        goalId: urlPath.includes('goal') ? id : undefined,
        organizationId: activeOrganization?.id,
      },
    },
  }), [id, urlPath, activeOrganization?.id]);

  const [getPortfolio, { startPolling, stopPolling }] = useLazyQuery(FETCH_PORTFOLIO_REPORTS, {
    fetchPolicy: 'no-cache',
    variables: queryVariables,
    onCompleted: (response: PortfolioReports<PortfolioReport[]>) => {
      const portfolios: PortfolioReport[] = response.fetchPortfolioReports.portfolioReports;
      const data = portfolios.length ? portfolios[0] : null;

      if (isEmpty(data)) {
        setNotFound(true);
        return;
      }

      // Only update portfolio if we haven't already started polling
      if (data.id && !data.refreshState) {
        updatePortfolio({ variables: { input: { portfolioReportId: data.id } } });
      }

      setPortfolio(data);
      setLoading(false);
    },
    onError: () => {
      setError(true);
      setLoading(false);
    },
  });

  const [updatePortfolio] = useMutation(REFRESH_PORTFOLIO_REPORT, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (response: RefreshPortfolioReport<PortfolioReport>) => {
      const data: PortfolioReport | null = response?.refreshPortfolioReport?.portfolioReport || null;
      if (isEmpty(data)) {
        setNotFound(true);
        return;
      }

      if (data?.refreshState === PortfolioReportRefreshStates.UPDATING) {
        startPolling(500);
      } else {
        stopPolling();
        setPortfolio(data);
      }
    },
    onError: () => {
      setError(true);
      setLoading(false);
      stopPolling();
    },
  });

  // Cleanup polling on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    setLoading(true);
    getPortfolio();
  }, [queryVariables, getPortfolio]);

  const [getSubTrades] = useLazyQuery(FETCH_SUB_TRADE_REQUESTS(true), {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        filter: {
          subAccountId: urlPath.includes('subAccount') ? id : undefined,
          goalId: urlPath.includes('goal') ? id : undefined,
          states: [SubTradeRequestStates.INITIATED, SubTradeRequestStates.REQUESTED, SubTradeRequestStates.READY],
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    onCompleted: (response: FetchSubTradeRequests<SubTradeRequest>) => {
      setSubTradeRequests(response?.fetchSubTradeRequests?.subTradeRequests || []);
      setLoading(false);
    },
    onError: () => {
      setError(true);
      setLoading(false);
    },
  });

  const [runPortfolioOptimizer] = useMutation(RUN_PORTFOLIO_OPTIMIZER, {
    onError: () => {
      setError(true);
    },
  });

  const [fetchOptimalPortfolio] = useLazyQuery(FETCH_OPTIMAL_PORTFOLIO, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        optimalPortfolioId: id,
      },
    },
    onCompleted: (response: FetchOptimalPortfolio<OptimalPortfolio>) => {
      const { optimalPortfolio } = response.fetchOptimalPortfolio;
      setOptimizedPortfolio(optimalPortfolio);
      setLoading(false);
    },
    onError: () => {
      setError(true);
      setLoading(false);
    },
  });

  const [fetchPortfolioOptimizer, { stopPolling: stopPollingOptimizer }] = useLazyQuery(FETCH_PORTFOLIO_OPTIMIZER, {
    pollInterval: 500,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (response: FetchPortfolioOptimizer<PortfolioOptimizer>) => {
      const { portfolioOptimizer } = response.fetchPortfolioOptimizer;

      if ([PortfolioOptimizerStates.CANCELED].includes(portfolioOptimizer?.state)) {
        stopPollingOptimizer();
        setLoading(false);
      }
      if ([PortfolioOptimizerStates.COMPLETED].includes(portfolioOptimizer?.state)) {
        stopPollingOptimizer();
        fetchOptimalPortfolio();
      }
    },
  });

  const [createSubTradeRequests] = useMutation(CREATE_SUB_TRADE_REQUESTS);
  const [pairSubTradeRequests] = useMutation(PAIR_SUB_TRADE_REQUESTS);
  const [bucketSubTradeRequests] = useMutation(BUCKET_SUB_TRADE_REQUESTS);

  useEffect(() => {
    if (!isEmpty(portfolio)) { getSubTrades(); }
  }, [portfolio, getSubTrades]);

  const subAccounts: PortfolioSubAccount[] = useMemo(() => {
    if (isEmpty(portfolio)) { return []; }

    const subAccountList: PortfolioSubAccount[] = (portfolio?.subAccounts || [])
      .reduce((total: PortfolioSubAccount[], subAccountReport: SubAccountReport) => {
        const { subAccount } = subAccountReport;
        if (!subAccount?.id) { return total; }
        return [...total, buildPortfolioSubAccount(subAccountReport, t)];
      }, []);

    subAccountList.sort(sortPortfolioReportSubAccounts);

    return subAccountList;
  }, [portfolio, t]);

  const securities: PortfolioSecurity[] = useMemo(() => {
    if (isEmpty(portfolio) || isEmpty(subAccounts)) { return []; }

    const securityList: PortfolioSecurity[] = (portfolio?.holdings || [])
      .reduce((total: PortfolioSecurity[], holding: HoldingReport): PortfolioSecurity[] => {
        const { financialProduct } = holding;

        if (!financialProduct?.id) return total;
        if (!holding.taxRank && holding.expectedValueCents === 0 && holding.valueCents === 0) return total;

        const subAccountWithHoldings = subAccounts.map((subAccount: PortfolioSubAccount) => {
          const subAccountHolding = subAccount.holdings.find((subAccountHoldings) => subAccountHoldings.financialProduct.id === financialProduct.id);
          return { ...subAccount, holding: subAccountHolding };
        });

        return [...total, buildSecurity(holding, subAccountWithHoldings)];
      }, []);

    securityList.sort(sortSecurities);

    return [{
      id: 'CASH',
      ticker: t('portfolioTable.cash'),
      expectedValueCents: portfolio?.expectedCashCents ?? 0,
      expectedPercentage: portfolio?.expectedCashPercentage ?? 0,
      currentPriceCents: 100,
      translatedName: t('canadianCash'),
      taxRank: null,
      financialProductTaxRank: 99,
      valueCents: 0,
      subAccounts,
      quantity: 0,
      pendingQuantity: 0,
    }, ...securityList];
  }, [portfolio, t, subAccounts]);

  const securityFilteredSubAccount = useCallback((subAccountId: string) => {
    let data: PortfolioSecurity[] = JSON.parse(JSON.stringify(securities));
    data = data.map((security: PortfolioSecurity) => {
      const item: PortfolioSecurity = security;
      item.subAccounts = item.subAccounts.filter((subAccount: PortfolioSubAccount) => {
        if (displayInactive) { return subAccount.id === subAccountId; }
        return subAccount.id === subAccountId && !subAccount.inactive;
      });
      return item;
    });
    return data;
  }, [securities, displayInactive]);

  const subAccountByInactive = useMemo(() => {
    let data: PortfolioSubAccount[] = JSON.parse(JSON.stringify(subAccounts));
    data = data.filter((subAccount: PortfolioSubAccount) => {
      if (displayInactive) { return true; }
      return !subAccount.inactive;
    });
    return data;
  }, [subAccounts, displayInactive]);

  const securityOptimized = useMemo((): PortfolioSecurity[] => {
    if (isEmpty(securities) || isEmpty(optimizedPortfolio)) { return []; }

    return securities.map((security: PortfolioSecurity) => {
      const securityCopy = security;
      securityCopy.subAccounts = security.subAccounts.map((subAccount: PortfolioSubAccount) => {
        const subAccountCopy = subAccount;
        subAccountCopy.optimalHolding = optimizedPortfolio?.optimalHoldings.find((optimalHolding: OptimalHolding) => (
          optimalHolding.financialProduct === subAccount.holding?.financialProduct?.id
        ));
        return subAccountCopy;
      });
      return securityCopy;
    });
  }, [securities, optimizedPortfolio]);

  const securitySubTradeRequests = useMemo((): PortfolioSecurity[] => {
    if (isEmpty(securities) || isEmpty(subTradeRequests)) { return []; }
    const readySubTradeRequests = subTradeRequests.filter((subTradeRequest) => subTradeRequest.state === SubTradeRequestStates.REQUESTED);

    return securities.map((security: PortfolioSecurity) => {
      const securityCopy = security;
      securityCopy.subAccounts = security.subAccounts.map((subAccount: PortfolioSubAccount) => {
        const subAccountCopy = subAccount;
        subAccountCopy.pendingSubtradeCashCents = readySubTradeRequests?.reduce((total: number, subtrade: SubTradeRequest) => {
          let result: number = total;
          if (subtrade?.financialProduct?.id !== subAccount.holding?.financialProduct?.id) { return total; }
          if (subtrade?.subAccount?.id !== subAccount?.id) { return total; }
          result += (subtrade?.preSplitMoneyAllocatedCents ?? subtrade.moneyAllocatedCents) * (subtrade.type === SubTradeRequestTypes.SELL ? -1 : 1);
          return result;
        }, 0);
        return subAccountCopy;
      });
      return securityCopy;
    });
  }, [securities, subTradeRequests]);

  const usdTickers = useMemo(() => {
    const results: Set<string> = new Set();
    if (isEmpty(subTradeRequests)) { return results; }

    const filteredSubtrades = subTradeRequests.filter((subTrade) => subTrade.state === SubTradeRequestStates.REQUESTED && activeOrganization?.allowPendingTransactions);
    filteredSubtrades.forEach((subTrade) => checkAndSetUsdTicker(subTrade, results));
    return results;
  }, [subTradeRequests, activeOrganization]);

  const mfSellAllTickers = useMemo(() => {
    const results: Set<string> = new Set();
    if (isEmpty(subTradeRequests)) { return results; }

    const filteredSubtrades = subTradeRequests.filter((subTrade) => subTrade.state === SubTradeRequestStates.REQUESTED && activeOrganization?.allowPendingTransactions);
    filteredSubtrades.forEach((subTrade) => checkAndSetMFSellAllTicker(subTrade, results));
    return results;
  }, [subTradeRequests, activeOrganization]);

  const illiquidProducts = useMemo(() => {
    const results: IlliquidProductsMap = new Map();
    if (isEmpty(subTradeRequests)) { return results; }

    subTradeRequests.forEach((subTrade) => {
      if (subTrade?.blockedUntil) buildIlliquidProduct(subTrade, results);
    });

    return results;
  }, [subTradeRequests]);

  const blockedUntilAccountActive = useMemo(() => {
    let results = false;
    if (isEmpty(subTradeRequests)) { return results; }

    subTradeRequests.forEach((subTrade) => {
      if (subTrade?.blockedUntilAccountActive) { results = true; }
    });

    return results;
  }, [subTradeRequests]);

  const blockedUntilSubAccountActive = useMemo(() => {
    let results = false;
    if (isEmpty(subTradeRequests)) { return results; }

    subTradeRequests.forEach((subTrade) => {
      if (subTrade?.blockedUntilSubAccountActive) { results = true; }
    });

    return results;
  }, [subTradeRequests]);

  const subTradeBySubAccount = useMemo(() => {
    const results = new Map<string, SubTradeRequest[]>();
    if (isEmpty(subTradeRequests)) { return results; }

    const filteredSubtrades = subTradeRequests.filter((subTrade) => subTrade.state === SubTradeRequestStates.REQUESTED && activeOrganization?.allowPendingTransactions);

    filteredSubtrades.forEach((subTrade) => {
      const subAccountId = subTrade.subAccount?.id;

      if (subAccountId) {
        if (!results.has(subAccountId)) { results.set(subAccountId, []); }
        results.get(subAccountId)?.push(subTrade);
      }
    });

    return results;
  }, [subTradeRequests, activeOrganization?.allowPendingTransactions]);

  const warnings = useMemo(() => {
    const results: PortfolioWarningsMap = (new Map());

    if (blockedUntilAccountActive) addPortfolioWarning(PortfolioWarningKeys.ACCOUNT_IS_BLOCKED, results);
    if (blockedUntilSubAccountActive) addPortfolioWarning(PortfolioWarningKeys.SUB_ACCOUNT_IS_BLOCKED, results);
    if (illiquidProducts.size) addPortfolioWarning(PortfolioWarningKeys.ILLIQUID_PRODUCTS, results, { datesAndTickers: illiquidProductsToString(illiquidProducts, localizedDate) });
    if (usdTickers.size) addPortfolioWarning(PortfolioWarningKeys.PENDING_TRANSACTION_USD_CURRENCY, results, { tickers: joinSetValues(usdTickers) });
    if (mfSellAllTickers.size) addPortfolioWarning(PortfolioWarningKeys.PENDING_TRANSACTION_MF_SELL_ALL, results, { tickers: joinSetValues(mfSellAllTickers) });

    return results;
  }, [usdTickers, mfSellAllTickers, illiquidProducts, blockedUntilAccountActive, blockedUntilSubAccountActive, localizedDate]);

  const runSecuritiesOptimizer = async (): Promise<void> => {
    if (!id) { return; }
    setLoading(true);

    const portfolioOptimizer = await runPortfolioOptimizer({
      variables: {
        input: {
          organizationId: activeOrganization.id,
          portfolioReportId: id,
          dryRun: true,
        },
      },
    });

    const portfolioOptimizerId = portfolioOptimizer?.data?.runPortfolioOptimizer?.portfolioOptimizer?.id ?? null;
    if (portfolioOptimizerId) fetchPortfolioOptimizer({ variables: { input: { portfolioOptimizerId } } });
  };

  const resetSecurityOptimized = (): void => { setOptimizedPortfolio(null); };

  const executeTrades = async (trades: DraftSubTradeRequest[]) => {
    const payload = trades.filter((trade) => trade.amountCents !== 0)
      .map((trade) => ({
        subAccountId: trade.subAccountId,
        financialProductId: trade.financialProductId,
        portfolioReportId: id,
        maxQuantity: trade.maxQuantity,
        moneyAllocatedCents: Math.abs(trade.amountCents),
        type: trade.type,
        isSellAll: trade.isSellAll,
      }));

    const type = urlPath.includes('goal') ? 'GOAL' : 'SUB_ACCOUNT';

    if (isEmpty(payload)) {
      showToast({ severity: 'info', message: t('components:generateTrades.tradesSent') });
      return;
    }

    try {
      setLoading(true);

      const response = await createSubTradeRequests({
        variables: {
          input: {
            source: type,
            sourceId: id,
            subTradeRequests: payload,
          },
        },
      });

      if (isEmpty(response.errors)) {
        const shouldBucket = activeOrganization?.bulkTradingProcess === OrgBulkTradingProcesses.BUCKET;
        const shouldPair = activeOrganization?.allowViewSubTradeRequestPairs
          && activeOrganization?.bulkTradingProcess === OrgBulkTradingProcesses.PAIR;

        if (shouldBucket) {
          await bucketSubTradeRequests({
            variables: { input: { source: type, sourceId: id } },
          });
        }

        if (shouldPair) {
          await pairSubTradeRequests({
            variables: { input: { source: type, sourceId: id } },
          });
        }

        showToast({ severity: 'info', message: t('components:generateTrades.tradesSent') });
      }

      setLoading(false);
    } catch (createError) {
      setError(true);
      setLoading(false);
    }
  };

  return {
    error,
    notFound,
    loading,
    portfolio,
    subAccounts,
    securities,
    securityFilteredSubAccount,
    securitySubTradeRequests,
    subAccountByInactive,
    displayInactive,
    setDisplayInactive,
    warnings,
    subTradeBySubAccount,
    runSecuritiesOptimizer,
    securityOptimized,
    resetSecurityOptimized,
    executeTrades,
  };
};
